import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, IS_DEN_DOMAIN, DEN_URL, apiFetch } from '../App';
import { Menu, X, Sun, Moon, User, LogOut, PawPrint } from 'lucide-react';
import NotificationBell from './NotificationBell';

const LOGO_COLOR = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png';
const LOGO_BLACK = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/4hfcpbdx_Petfolk%20Logo%20Black%20on%20transparentBG.png';

export default function Header() {
  const { user, logout, setShowAuth } = useAuth();
  const { darkMode, toggleDark } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const isTransparent = location.pathname === '/';

  // Fetch active stores once for the hamburger menu (so newly added stores auto-appear)
  useEffect(() => {
    apiFetch('/api/nodes')
      .then(nodes => {
        const sorted = nodes
          .filter(n => n.type === 'STORE' && n.active && !n.comingSoon)
          .sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '') || (a.localName || '').localeCompare(b.localName || ''));
        setStores(sorted);
      })
      .catch(e => console.error('Failed to load stores for menu:', e));
  }, []);

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: isTransparent ? 'rgba(255,251,245,0.7)' : 'var(--bg-card)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: isTransparent ? 'none' : '1px solid var(--border-color)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between relative">
        {/* Left: Hamburger */}
        <button
          data-testid="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* Center: Logo — absolutely centered so it's always perfectly in the middle */}
        <Link
          to="/"
          data-testid="logo-link"
          className="absolute left-1/2 -translate-x-1/2 flex items-center"
        >
          <img
            src={LOGO_COLOR}
            alt="Petfolk"
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* Right: Actions (no theme toggle here) */}
        <div className="flex items-center gap-2">
          {user && <NotificationBell />}

          {user ? (
            <div className="relative group">
              <button
                data-testid="user-menu-btn"
                className="flex items-center gap-2 p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{user.name?.[0] || 'P'}</span>
                </div>
              </button>
              <div className="absolute right-0 top-full mt-2 w-56 petfolk-card p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-xl">
                <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{user.phone}</p>
                </div>
                <Link to="/paws" className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: 'var(--text-primary)' }}>
                  <PawPrint size={16} /> My Paws
                </Link>
                {user.role !== 'CUSTOMER' && (
                  <a href={DEN_URL} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: 'var(--text-primary)' }}>
                    <User size={16} /> Admin Den ↗
                  </a>
                )}
                <button onClick={logout} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 w-full text-left transition-colors">
                  <LogOut size={16} /> Sign Out
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={() => setShowAuth(true)}
              className="btn-primary text-sm py-2 px-4"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Mobile / Hamburger Menu */}
      {menuOpen && (
        <div className="absolute top-16 left-0 right-0 p-4 shadow-xl animate-slide-up" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)' }} data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              Home
            </Link>

            {stores.length > 0 && (
              <>
                <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  Our Stores
                </p>
                {stores.map(s => (
                  <Link
                    key={s.id}
                    to={`/${s.slug}`}
                    onClick={() => setMenuOpen(false)}
                    className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex items-center justify-between"
                    style={{ color: 'var(--text-primary)' }}
                    data-testid={`menu-store-${s.slug}`}
                  >
                    <span>{(s.displayName || '').replace(/^Petfolk\s*/, '') || s.localName || s.slug}</span>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.cityName}</span>
                  </Link>
                ))}
              </>
            )}

            <div className="my-2" style={{ borderTop: '1px solid var(--border-color)' }} />

            <Link
              to="/franchise"
              onClick={() => setMenuOpen(false)}
              className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              Franchise
            </Link>
            <Link
              to="/blog"
              onClick={() => setMenuOpen(false)}
              data-testid="menu-blog"
              className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              Blog
            </Link>
            <Link
              to="/support"
              onClick={() => setMenuOpen(false)}
              className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              Support
            </Link>
            {user && (
              <Link
                to="/paws"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                style={{ color: 'var(--petfolk-orange)' }}
              >
                My Paws Account
              </Link>
            )}
            {/* Theme toggle — last item in menu */}
            <button
              data-testid="theme-toggle"
              onClick={() => { toggleDark(); setMenuOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors w-full text-left border-t mt-1 pt-3"
              style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
